import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { useOrganizationContext } from '@openx/components-organization/lib/utils/context/OrganizationContext.tsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiKeysTable } from '../components/ApiKeysTable.tsx';
import { TabPanel } from '../components/TabPanel.tsx';

const tabsStylesData: SxProps<Theme> = {
  borderRight: 1,
  borderColor: 'divider',
  height: 'max-content',
  borderBottom: 'none',
  '& .MuiTab-textColorPrimary': {
    alignItems: 'flex-start',
  },
  '& .MuiTab-textColorPrimary.Mui-selected': {
    color: 'primary.main',
  },
};

type OrganizationDetailsSectionProps = {
  accountId: string;
};

export const OrganizationDetailsSection = ({ accountId }: OrganizationDetailsSectionProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<number>(0);
  const { useOrganizationApiKeysFetch } = useOrganizationContext();
  const { apiKeys, loading } = useOrganizationApiKeysFetch(accountId);

  return (
    <>
      <FormSection title={t('Organization Details')}>
        <Alert severity="warning" data-test="api-keys-alert" sx={{ ml: 0.5 }}>
          {t('API Keys are generated for the whole organization, so store them securely.')}
        </Alert>
      </FormSection>

      <Grid container>
        <Grid size={3}>
          <Tabs
            onChange={(_, val) => setActiveTab(val)}
            orientation="vertical"
            sx={tabsStylesData}
            variant="scrollable"
            value={activeTab}
          >
            <Tab data-test="api-key-list-tab" label={t('API Keys List')} id={'vertical-tabpanel-0'} />
            <Tab data-test="expired-api-key-list-tab" label={t('Expired API Keys List')} id={'vertical-tabpanel-1'} />
          </Tabs>
        </Grid>

        <Grid size={9}>
          <TabPanel index={0} value={activeTab}>
            <ApiKeysTable accountId={accountId} apiKeys={apiKeys} loading={loading} showActiveKeys />
          </TabPanel>

          <TabPanel index={1} value={activeTab}>
            <ApiKeysTable accountId={accountId} apiKeys={apiKeys} loading={loading} />
          </TabPanel>
        </Grid>
      </Grid>
    </>
  );
};
